import { X, CreditCard, Banknote, QrCode, Gift, Package } from 'lucide-react';
import type { Order } from '../../../api/queries/order.queries';

interface OrderDetailsModalProps {
  order: Order;
  onClose: () => void;
}

function OrderDetailsModal({ order, onClose }: OrderDetailsModalProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
    }).format(amount);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'ready':
        return 'bg-green-100 text-green-800';
      case 'picked_up':
        return 'bg-purple-100 text-purple-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
      case 'voided':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentIcon = (method: string) => {
    switch (method) {
      case 'cash':
        return <Banknote className="h-4 w-4" />;
      case 'card':
        return <CreditCard className="h-4 w-4" />;
      case 'qr':
        return <QrCode className="h-4 w-4" />;
      case 'loyalty_points':
        return <Gift className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getSizeLabel = (size: string) => {
    switch (size) {
      case 'S':
        return 'Small';
      case 'M':
        return 'Medium';
      case 'L':
        return 'Large';
      default:
        return size;
    }
  };

  const getBranchName = (branchId: Order['branchId']) => {
    if (typeof branchId === 'object' && branchId !== null) {
      return `${branchId.name} (${branchId.code})`;
    }
    return branchId;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-lg bg-card flex flex-col">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-xl font-bold">Order {order.orderNumber}</h2>
            <p className="text-sm text-muted-foreground">{formatDate(order.createdAt)}</p>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${getStatusStyle(
                order.status
              )}`}
            >
              {order.status.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
            </span>
            <button
              onClick={onClose}
              className="rounded-full p-2 hover:bg-secondary"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Order Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg bg-muted p-3">
              <div className="text-sm text-muted-foreground">Branch</div>
              <div className="font-medium">{getBranchName(order.branchId)}</div>
            </div>
            <div className="rounded-lg bg-muted p-3">
              <div className="text-sm text-muted-foreground">Created By</div>
              <div className="font-medium">
                {order.createdBy
                  ? typeof order.createdBy === 'object'
                    ? order.createdBy.username
                    : order.createdBy
                  : 'Unknown'}
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div>
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <Package className="h-4 w-4" />
              Order Items ({order.items.length})
            </h3>
            <div className="space-y-2">
              {order.items.map((item, idx) => (
                <div
                  key={idx}
                  className="rounded-lg border bg-background p-3"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium">{item.menuItemName}</div>
                      <div className="text-sm text-muted-foreground mt-1 space-x-2">
                        <span>{getSizeLabel(item.size)}</span>
                        <span>•</span>
                        <span>{item.sweetnessLevel}% Sweet</span>
                        <span>•</span>
                        <span>Qty: {item.quantity}</span>
                      </div>
                      {item.toppings && item.toppings.length > 0 && (
                        <div className="text-sm text-muted-foreground mt-1">
                          Toppings: {item.toppings.map((t) => t.name).join(', ')}
                        </div>
                      )}
                      {item.notes && (
                        <div className="text-sm text-orange-600 mt-1">
                          Note: {item.notes}
                        </div>
                      )}
                      {item.wasAutoSwitched && (
                        <div className="text-xs text-blue-600 mt-1">
                          (Recipe auto-switched due to stock)
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="font-bold">{formatCurrency(item.totalPrice)}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatCurrency(item.unitPrice)} each
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Summary */}
          <div>
            <h3 className="font-medium mb-3">Payment Summary</h3>
            <div className="rounded-lg border bg-background p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{formatCurrency(order.subtotal)}</span>
              </div>
              {order.discount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Discount</span>
                  <span>-{formatCurrency(order.discount)}</span>
                </div>
              )}
              {order.tax > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tax</span>
                  <span>{formatCurrency(order.tax)}</span>
                </div>
              )}
              <div className="border-t pt-2 flex justify-between font-bold text-lg">
                <span>Total</span>
                <span>{formatCurrency(order.total)}</span>
              </div>
            </div>
          </div>

          {/* Payments */}
          {order.payments && order.payments.length > 0 && (
            <div>
              <h3 className="font-medium mb-3">Payments</h3>
              <div className="space-y-2">
                {order.payments.map((payment, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg border bg-background p-3 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-muted p-2">
                        {getPaymentIcon(payment.method)}
                      </div>
                      <div>
                        <div className="font-medium capitalize">
                          {payment.method.replace('_', ' ')}
                        </div>
                        {payment.reference && (
                          <div className="text-xs text-muted-foreground">
                            Ref: {payment.reference}
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground">
                          {formatDate(payment.paidAt)}
                        </div>
                      </div>
                    </div>
                    <div className="font-bold">{formatCurrency(payment.amount)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment Status */}
          <div className="rounded-lg p-3 flex items-center justify-between bg-muted">
            <span className="font-medium">Payment Status</span>
            <span
              className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
                order.isPaidInFull
                  ? 'bg-green-100 text-green-800'
                  : 'bg-orange-100 text-orange-800'
              }`}
            >
              {order.isPaidInFull ? 'Paid in Full' : 'Payment Pending'}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 shrink-0">
          <button
            onClick={onClose}
            className="w-full rounded-lg border bg-background px-4 py-2 hover:bg-secondary transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default OrderDetailsModal;
